// PDF.js loaded from CDN to reduce Heroku slug size (~37MB saving)
// Using ES module version from cdnjs
const PDFJS_VERSION = '4.0.379'
const PDFJS_CDN_BASE = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}`

let pdfjsLib = null

/**
 * Lazily load PDF.js from CDN on first use
 */
async function getPdfJs() {
  if (pdfjsLib) return pdfjsLib

  // Dynamic import from CDN
  pdfjsLib = await import(/* webpackIgnore: true */ `${PDFJS_CDN_BASE}/pdf.min.mjs`)

  // Set up the worker from CDN
  pdfjsLib.GlobalWorkerOptions.workerSrc = `${PDFJS_CDN_BASE}/pdf.worker.min.mjs`

  return pdfjsLib
}

/**
 * Generate a thumbnail image from a PDF file
 * @param {File} file - The PDF file
 * @param {number} maxSize - Maximum width/height of thumbnail (default 200)
 * @returns {Promise<string>} - Data URL of the thumbnail image
 */
export async function generatePdfThumbnail(file, maxSize = 800) {
  try {
    // Load PDF.js from CDN (lazy load on first use)
    const pdfjs = await getPdfJs()

    // Read file as ArrayBuffer
    const arrayBuffer = await file.arrayBuffer()

    // Load the PDF
    const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise

    // Get the first page
    const page = await pdf.getPage(1)

    // Render at 2x scale for crisp thumbnails on retina displays
    const baseViewport = page.getViewport({ scale: 1 })
    const scale = Math.min(maxSize / baseViewport.width, maxSize / baseViewport.height) * 2
    const scaledViewport = page.getViewport({ scale })

    // Create canvas
    const canvas = document.createElement('canvas')
    canvas.width = scaledViewport.width
    canvas.height = scaledViewport.height

    // Render page to canvas
    const context = canvas.getContext('2d')
    await page.render({
      canvasContext: context,
      viewport: scaledViewport
    }).promise

    // Convert to data URL (use PNG for better quality)
    return canvas.toDataURL('image/png')
  } catch (error) {
    console.error('PDF thumbnail generation failed:', error)
    throw error
  }
}

/**
 * Rasterize the first page of a PDF to a full-size JPEG File, suitable for
 * uploading as an image (e.g. a floor plan). Rightmove and the website only
 * accept image floor plans, so we convert client-side before upload — no
 * server-side Ghostscript dependency.
 * @param {File} file - The PDF file
 * @param {Object} opts - { maxSize = 2000, quality = 0.92 }
 * @returns {Promise<File>} - A JPEG File of the first page
 */
export async function renderPdfFirstPageToImageFile(file, opts = {}) {
  const maxSize = opts.maxSize || 2000
  const quality = opts.quality || 0.92

  const pdfjs = await getPdfJs()
  const arrayBuffer = await file.arrayBuffer()
  const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise
  const page = await pdf.getPage(1)

  const baseViewport = page.getViewport({ scale: 1 })
  const fit = Math.min(maxSize / baseViewport.width, maxSize / baseViewport.height)
  const viewport = page.getViewport({ scale: fit > 0 ? fit : 1 })

  const canvas = document.createElement('canvas')
  canvas.width = Math.round(viewport.width)
  canvas.height = Math.round(viewport.height)

  const context = canvas.getContext('2d')
  // Flatten onto white — transparent PDF regions would otherwise go black in JPEG.
  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, canvas.width, canvas.height)

  await page.render({ canvasContext: context, viewport }).promise

  const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality))
  const baseName = (file.name || 'floor-plan').replace(/\.[^.]+$/, '')
  return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' })
}

// Make available globally for Alpine.js
window.generatePdfThumbnail = generatePdfThumbnail
window.renderPdfFirstPageToImageFile = renderPdfFirstPageToImageFile

// Retry a server-generated thumbnail <img> that failed to load.
//
// There is a sub-second window after a thumbnail job finishes where the
// variant's database record exists (so thumbnail_url is served) but its
// S3 object hasn't finished uploading — an <img> that fetches in that gap
// gets a 404 and browsers never retry a failed src. Backed-off retries with
// a cache-busting param cover the gap; after three failures the tile falls
// back to the document icon (doc.thumbnail_failed drives the Alpine template).
window.retryDocumentThumbnail = (img, doc) => {
  doc._thumbnailRetries = (doc._thumbnailRetries || 0) + 1
  if (doc._thumbnailRetries > 3) {
    doc.thumbnail_failed = true
    return
  }
  const separator = doc.thumbnail_url.includes('?') ? '&' : '?'
  const url = `${doc.thumbnail_url}${separator}retry=${doc._thumbnailRetries}`
  setTimeout(() => { img.src = url }, 1000 * doc._thumbnailRetries)
}
